import { useEffect, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { obterSocket } from "../../lib/socket.js";

export interface Progresso {
  percent: number;
  step: string;
}

/**
 * Acompanha uma geração em andamento.
 *
 * Socket **e** polling, de propósito. O socket dá a barra de progresso ao vivo;
 * o polling garante que a tela destrava mesmo se a conexão cair. O sistema
 * anterior dependia só do socket, e quando ele caía a tela ficava presa em
 * "processando" indefinidamente — o operador só descobria dando refresh.
 */
export function useGeracao(id: number | null) {
  const [progresso, setProgresso] = useState<Progresso | null>(null);
  const utils = trpc.useUtils();

  const consulta = trpc.generations.getById.useQuery(
    { id: id ?? 0 },
    {
      enabled: id !== null,
      // A rede de salão de evento cai; 4s é o teto de tempo que o operador fica
      // sem saber que a imagem já ficou pronta — ou que a foto saiu da fila do WhatsApp.
      refetchInterval: (q) =>
        q.state.data?.imageStatus === "processing" || q.state.data?.whatsappEstado === "na_fila"
          ? 4_000
          : false,
    },
  );

  const processando = consulta.data?.imageStatus === "processing";

  useEffect(() => {
    if (id === null) return;

    const socket = obterSocket();

    const aoProgredir = (e: { generationId: number; percent: number; step: string }) => {
      if (e.generationId !== id) return;
      setProgresso({ percent: e.percent, step: e.step });
    };

    const aoAtualizar = (e: { generationId: number }) => {
      if (e.generationId !== id) return;
      void utils.generations.getById.invalidate({ id });
    };

    socket.on("job:progress", aoProgredir);
    socket.on("generation:updated", aoAtualizar);

    return () => {
      socket.off("job:progress", aoProgredir);
      socket.off("generation:updated", aoAtualizar);
    };
  }, [id, utils]);

  // Sai de "processando" e o progresso perde o sentido: limpar evita que a
  // barra reapareça congelada na próxima geração.
  useEffect(() => {
    if (!processando) setProgresso(null);
  }, [processando]);

  return {
    geracao: consulta.data ?? null,
    carregando: consulta.isLoading,
    processando,
    progresso,
    recarregar: () => void consulta.refetch(),
  };
}
